import { useAxios } from '@/context/axiosContext'
import { useDialog } from '@/context/dialogContext'
import useCopyToClipboard from '@/hooks/useCopyToClipboard'
import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import {
  formatDate,
  GolfBizNm,
  GolfInfoData,
  PromotionData,
  PromotionTitle,
} from '../promotionItem'
import './promotion.css'
import { Badge, Button } from '@/components'
import {
  buttonName,
  confirmMessages,
  getBadgeLabel,
  getMobileBadgeClass,
  getPromoBadgeLabel,
  successMessages,
  withDrawnCumtomer,
} from '@/pages/board/boardItem'
import { Viewer } from '@toast-ui/react-editor'
import { useAppSelector } from '@/redux/hooks'
import { UpdatePromotion } from './updatePromotion'
import commonUtils from '@/utils/commonUtils'
import { useResponsive } from '@/hooks/useResponsive'

export const DetailPromotion = () => {
  const axios = useAxios()
  const location = useLocation()
  const navigate = useNavigate()
  const commentRef = useRef<HTMLDivElement>(null)
  const { isAuthenticated, user } = useAppSelector((state) => state.auth)
  const [isDisabled, setIsDisabled] = useState(true) // 초기값은 비활성화
  const [isEditMode, setIsEditMode] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const { showAlert, showConfirm } = useDialog()
  const [blogNttCn, setBlogNttCn] = useState<string>('')
  const [searchParams] = useSearchParams() // 쿼리 파라미터 사용
  const [isCopied, copyToClipboard] = useCopyToClipboard()
  const nttSn = Number(searchParams.get('nttSn')) // 게시판 번호
  const [imgUrls, setImgUrls] = useState<string[]>([])
  const [nameList, setNameList] = useState<GolfBizNm[]>([])
  const [isThumbnailChanged, setIsThumbnailChanged] = useState(false)
  const { isMobile, isTablet, isDesktop } = useResponsive()
  const [previousThumbnailSeq, setPreviousThumbnailSeq] = useState<
    number | null
  >(null)
  const [fileList, setFileList] = useState<
    {
      fileSeq: number
      fileName: string
      fileSource: string
      fileOrgName: string
    }[]
  >([])
  const [promoDetail, setPromoDetail] = useState<PromotionData>({
    adminDelYn: '',
    bizCd: '',
    cmpnyCd: '',
    cstId: '',
    delYn: '',
    fileNm: '',
    fileSeq: 0,
    fileSeqList: [],
    fileNameList: [],
    fileSourceList: [],
    deleteSeqList: [],
    nttCn: '',
    nttSj: '',
    nttSn: 0,
    page: 1,
    popupYn: '',
    prmtEndDate: '',
    prmtFileName: '',
    prmtFileSeq: 0,
    prmtNttSn: 0,
    prmtStartDate: '',
    prmtType: '',
    prmtUrl: '',
    readngCnt: 0,
    regDate: '',
    regId: '',
    tpPrmtYn: '',
    updDate: '',
    updId: '',
    useYn: '',
    nickName: '',
    withDrawYn: '',
    thumbnailSeq: 0,
    thumbnail: '',
    fileOrgNm: '',
  })

  const withDrawYn = promoDetail?.withDrawYn === 'Y'

  useEffect(() => {
    getPromoDetail()
  }, [nttSn])

  useEffect(() => {
    window.scrollTo(0, 0) // behavior: 'smooth' 없이 즉시 이동
  }, [])

  useEffect(() => {
    if (promoDetail?.thumbnailSeq) {
      setPreviousThumbnailSeq(promoDetail.thumbnailSeq)
    }
  }, [promoDetail])

  const extractImageUrls = (htmlContent: string) => {
    const imgUrls = Array.from(
      new DOMParser()
        .parseFromString(htmlContent, 'text/html')
        .querySelectorAll('img'),
    ).map((img) => {
      const imgSrc = img.src
      const filePath = imgSrc.split('upload')[1]
      return decodeURIComponent(filePath)
    })
    return imgUrls
  }

  const getPromoDetail = async () => {
    try {
      const response = await axios.get(`/promotion/detail`, {
        headers: { 'Cache-Control': 'no-cache' },
        params: { nttSn },
      })

      const { promoDetail, fileList, golfNameList } = response.data.data // 응답에서 data 추출

      // 호출 예시
      const htmlContent = promoDetail.nttCn // 서버에서 받은 HTML 콘텐츠
      const imgUrls = extractImageUrls(htmlContent)

      if (!response.data || !promoDetail) {
        return
      }

      const fileInfoList = fileList.map((file: any) => ({
        fileName: file.fileNm,
        fileSeq: file.fileSeq,
      }))

      const seenBizCds = new Set<string>()
      const nameList: GolfBizNm[] = golfNameList
        .filter((item: GolfInfoData) => {
          const isValid = item.remark?.trim()
          const isUnique = !seenBizCds.has(item.bizCd || '')

          if (isValid && isUnique) {
            seenBizCds.add(item.bizCd || '')
            return true
          }
          return false
        })
        .map((item: GolfInfoData) => ({
          areaCd: item.areaCd?.trim(),
          bizNm: item.bizNm?.trim(),
          bizCd: item.bizCd,
        }))

      setFileList(fileInfoList)
      setImgUrls(imgUrls)
      setPromoDetail(promoDetail)
      setNameList(nameList)
    } catch (err: any) {
      console.error('Error fetching detailBlog:', err)
    }
  }

  const endDateUnlimited = promoDetail?.prmtEndDate?.startsWith('2099-12-31')

  const handleModalAction = (type: 'update' | 'delete' | 'cancel') => {
    if (type === 'update') {
      showConfirm({
        title: confirmMessages.update,
        confirmBtnName: buttonName.confirm,
        cancelBtnName: buttonName.cancel,
        onConfirm() {
          setIsEditMode(true)
        },
      })
    }

    if (type === 'delete') {
      showConfirm({
        title: confirmMessages.delete,
        confirmBtnName: buttonName.confirm,
        cancelBtnName: buttonName.cancel,
        onConfirm() {
          deletePromo()
        },
      })
    }

    if (type === 'cancel') {
      showConfirm({
        title: confirmMessages.cancel,
        confirmBtnName: buttonName.confirm,
        cancelBtnName: buttonName.cancel,
        onConfirm() {
          setTimeout(() => {
            navigate(0) // 500ms 후에 페이지 이동
          }, 100)
        },
      })
    }
  }

  const deletePromo = async () => {
    try {
      await axios.post(`/promotion/delete`, { nttSn })

      showAlert({
        type: 'success',
        title: successMessages.delete,
        btnName: buttonName.confirm,
        onClose() {
          setTimeout(() => {
            navigate(`/promotion`)
          }, 100)
        },
      })
    } catch (err: any) {
      console.error('Error fetching detailBlog:', err)
    }
  }

  const handleCopy = () => {
    const url = window.location.href // 현재 페이지 URL
    copyToClipboard(url)
  }

  return (
    <>
      {!isEditMode ? (
        <>
          {!isMobile ? (
            <>
              <div className="promo-header">
                <div className="promo-title">{PromotionTitle}</div>
                <Button
                  color="neverbe"
                  onClick={() => {
                    setTimeout(() => {
                      navigate(`/promotion`)
                    }, 100)
                  }}
                >
                  목록
                </Button>
              </div>
              <div className="detail-wrapper">
                <div className="detail-box">
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <div>
                      <Badge
                        color={
                          promoDetail.prmtType === '01'
                            ? 'success'
                            : promoDetail.prmtType === '02'
                              ? 'warning'
                              : 'primary' // '03' 또는 그 외
                        }
                        marginRight={'4px'}
                        padding={'5px'}
                      >
                        <span style={{ fontWeight: 'bold', fontSize: '11px' }}>
                          {promoDetail.prmtType === '01'
                            ? '골프장'
                            : promoDetail.prmtType === '02'
                              ? '스크린'
                              : '기타'}
                        </span>
                      </Badge>
                      {promoDetail.prmtType === '01' && (
                        <Badge color="neverbe" padding={'5px'}>
                          <span
                            style={{ fontWeight: 'bold', fontSize: '11px' }}
                          >
                            {promoDetail.bizNm}
                          </span>
                        </Badge>
                      )}
                    </div>
                    <div
                      style={{ color: 'rgb(208, 157, 48)', fontWeight: 'bold' }}
                    >
                      프로모션 기간 :&nbsp;
                      {endDateUnlimited
                        ? `${formatDate(promoDetail?.prmtStartDate || '', true)} ~ 종료일 미정`
                        : `${formatDate(promoDetail.prmtStartDate || '', true)} ~ ${formatDate(promoDetail.prmtEndDate || '', true)}`}
                    </div>
                  </div>
                  <div className="detail-title">{promoDetail?.nttSj}</div>
                  <div className="detail-promo-meta">
                    <div className="detail-top">
                      <div>
                        <div>
                          작성자 :&nbsp;
                          {withDrawYn
                            ? withDrawnCumtomer.name
                            : promoDetail?.nickName}
                          &nbsp;
                        </div>
                        <div className="detail-sub">
                          <div>
                            작성일 :&nbsp;
                            {promoDetail?.regDate
                              ? commonUtils.formatDate(
                                  promoDetail.regDate,
                                  true,
                                )
                              : ''}
                          </div>
                          <div className="promo-divider" />
                          <div>조회 : {promoDetail?.readngCnt}</div>
                        </div>
                      </div>
                      {promoDetail?.prmtUrl ? (
                        <div style={{ display: 'flex' }}>
                          <a
                            href={promoDetail.prmtUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Button color="neverbe" variant="outlined">
                              프로모션 바로가기
                            </Button>
                          </a>
                        </div>
                      ) : (
                        <div className="url-copy" onClick={handleCopy}>
                          URL복사
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="detail-divider" />
                  <div className="detail-content">
                    <Viewer
                      key={promoDetail.nttSn}
                      initialValue={promoDetail.nttCn}
                    />
                  </div>
                </div>
                <div className="detail-button-wrap">
                  <div className="button-group">
                    {isDisabled ? (
                      <div className="button-row">
                        {user?.cstId === promoDetail?.regId && (
                          <>
                            <Button
                              color="neverbe"
                              onClick={() => handleModalAction('update')}
                            >
                              수정
                            </Button>
                            <Button
                              color="neverbe"
                              onClick={() => handleModalAction('delete')}
                            >
                              삭제
                            </Button>
                          </>
                        )}
                        <Button
                          color="neverbe"
                          onClick={() =>
                            navigate(`/promotion`, { state: location.state })
                          }
                        >
                          목록
                        </Button>
                      </div>
                    ) : (
                      <div className="button-row">
                        <Button
                          color="neverbe"
                          onClick={() => handleModalAction('cancel')}
                        >
                          취소
                        </Button>
                        <Button
                          color="neverbe"
                          onClick={() =>
                            navigate(`/promotion`, { state: location.state })
                          }
                        >
                          목록
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="mobile-view">
              <div className="mobile-post-detail-container">
                <div className="mobile-post-detail-header">
                  <h1
                    className="mobile-post-detail-title"
                    onClick={() => {
                      navigate(`/promotion`)
                    }}
                  >
                    정보
                  </h1>
                  {promoDetail.prmtUrl ? (
                    <div>
                      <a
                        href={promoDetail.prmtUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <button className="mobile-url-copy-btn">
                          프로모션 바로가기
                        </button>
                      </a>
                    </div>
                  ) : (
                    <button
                      className="mobile-url-copy-btn"
                      onClick={handleCopy}
                    >
                      URL복사
                    </button>
                  )}
                </div>
                <div className="mobile-post-card" key={promoDetail?.nttSn}>
                  <div className="mobile-post-card-header">
                    <span
                      className={getMobileBadgeClass(
                        promoDetail?.prmtType || '00',
                      )}
                    >
                      {getPromoBadgeLabel(promoDetail?.prmtType ?? '기타')}
                    </span>
                    &nbsp;
                    {promoDetail.prmtType === '01' && (
                      <span className={getMobileBadgeClass('01')}>
                        {promoDetail.bizNm}
                      </span>
                    )}
                    <h2 className="mobile-post-card-title">
                      {promoDetail?.nttSj}
                    </h2>
                    <div className="mobile-post-card-meta">
                      <div className="mobile-post-card-meta-row">
                        <span style={{ color: '#d09D03', fontWeight: 'bold' }}>
                          &nbsp;
                          {withDrawYn
                            ? withDrawnCumtomer.name
                            : promoDetail?.nickName}
                        </span>
                        <span>views {promoDetail?.readngCnt}</span>
                      </div>
                      <div className="mobile-post-card-meta-row">
                        <span>
                          &nbsp;
                          {promoDetail?.regDate
                            ? commonUtils.formatDate(promoDetail.regDate, true)
                            : ''}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="mobile-post-card-content">
                    <Viewer
                      key={promoDetail.nttSn}
                      initialValue={promoDetail.nttCn}
                    />
                  </div>
                </div>
                <div className="mobile-post-actions">
                  <button
                    className="mobile-action-btn primary"
                    onClick={() =>
                      navigate(`/promotion`, { state: location.state })
                    }
                  >
                    목록으로
                  </button>
                  {(user?.userId === promoDetail?.regId ||
                    user?.cstId === promoDetail?.regId) && (
                    <>
                      <button
                        className="mobile-action-btn user"
                        onClick={() => handleModalAction('update')}
                      >
                        수정
                      </button>
                      <button
                        className="mobile-action-btn user"
                        onClick={() => handleModalAction('delete')}
                      >
                        삭제
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <UpdatePromotion
          nttSn={nttSn}
          nttSj={promoDetail?.nttSj}
          nttCn={promoDetail?.nttCn}
          prmtType={promoDetail?.prmtType}
          bizCd={promoDetail?.bizCd}
          thumbnail={promoDetail?.thumbnail}
          thumbnailSeq={promoDetail?.thumbnailSeq}
          fileOrgNm={promoDetail?.fileOrgNm}
          prmtStartDate={formatDate(promoDetail.prmtStartDate || '')}
          prmtEndDate={formatDate(promoDetail.prmtEndDate || '')}
          prmtUrl={promoDetail.prmtUrl}
          detailFileList={fileList}
          nameList={nameList}
          //endDateUnlimited={promoDetail?.prmtEndDate?.startsWith('2099-12-31')}
          endDateUnlimited={endDateUnlimited}
        />
      )}
    </>
  )
}
